import { useState } from 'react';
import './ToolStyles.css';

type PaymentType = 'equal-installment' | 'equal-principal';

const formatMoney = (n: number): string =>
    n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MortgageCalculator = () => {
    // 使用字符串存储贷款金额，避免 type="number" 输入框的 backspace/前导零问题
    const [amountStr, setAmountStr] = useState('1000000');
    const [rateStr, setRateStr] = useState('3.5');
    const [years, setYears] = useState(30);
    const [type, setType] = useState<PaymentType>('equal-installment');
    const [result, setResult] = useState<{
        monthly: number;
        total: number;
        totalInterest: number;
        schedule: { month: number; payment: number; principal: number; interest: number; balance: number }[];
    } | null>(null);

    // 解析为数字用于计算，空字符串视为 0
    const amount = parseFloat(amountStr) || 0;
    const rate = parseFloat(rateStr) || 0;

    const calculate = () => {
        if (amount <= 0 || rate <= 0) return;
        const months = years * 12;
        const monthlyRate = rate / 100 / 12;
        const schedule: { month: number; payment: number; principal: number; interest: number; balance: number }[] = [];

        if (type === 'equal-installment') {
            // 等额本息: 每月还款 = [贷款本金 × 月利率 × (1+月利率)^月数] / [(1+月利率)^月数 - 1]
            const pow = Math.pow(1 + monthlyRate, months);
            const monthly = (amount * monthlyRate * pow) / (pow - 1);
            let balance = amount;
            for (let i = 1; i <= Math.min(months, 12); i++) {
                const interest = balance * monthlyRate;
                const principal = monthly - interest;
                balance -= principal;
                schedule.push({ month: i, payment: monthly, principal, interest, balance: Math.max(0, balance) });
            }
            // 后续月份不逐条展示，直接算总数
            const fullMonthly = monthly;
            const fullTotalInterest = fullMonthly * months - amount;
            setResult({
                monthly: fullMonthly,
                total: fullMonthly * months,
                totalInterest: fullTotalInterest,
                schedule: schedule.slice(0, 12), // 只展示前12个月明细
            });
        } else {
            // 等额本金: 每月本金 = 贷款本金 / 月数，每月利息 = 剩余本金 × 月利率
            const monthlyPrincipal = amount / months;
            let balance = amount;
            const firstMonthly = monthlyPrincipal + balance * monthlyRate;
            for (let i = 1; i <= Math.min(months, 12); i++) {
                const interest = balance * monthlyRate;
                const payment = monthlyPrincipal + interest;
                balance -= monthlyPrincipal;
                schedule.push({ month: i, payment, principal: monthlyPrincipal, interest, balance: Math.max(0, balance) });
            }
            const fullTotalInterest = (amount * monthlyRate * (months + 1)) / 2;
            setResult({
                monthly: firstMonthly,
                total: amount + fullTotalInterest,
                totalInterest: fullTotalInterest,
                schedule,
            });
        }
    };

    return (
        <div className="tool-container">
            <div className="input-group">
                <label className="input-label">贷款金额（元）</label>
                <input
                    className="tool-input"
                    type="text"
                    inputMode="numeric"
                    value={amountStr}
                    onChange={(e) => {
                        // 只保留数字，过滤其他字符
                        const raw = e.target.value.replace(/\D/g, '');
                        setAmountStr(raw);
                    }}
                    placeholder="请输入贷款金额"
                />
            </div>

            <div className="button-group">
                {[
                    { value: '500000', label: '50万' },
                    { value: '1000000', label: '100万' },
                    { value: '2000000', label: '200万' },
                    { value: '3000000', label: '300万' },
                ].map((preset) => (
                    <button
                        key={preset.value}
                        className={`mode-button ${amountStr === preset.value ? 'active' : ''}`}
                        onClick={() => setAmountStr(preset.value)}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>

            <div className="input-group">
                <label className="input-label">年利率 (%)</label>
                <input
                    className="tool-input"
                    type="text"
                    inputMode="decimal"
                    value={rateStr}
                    onChange={(e) => {
                        // 只保留数字和小数点，过滤其他字符
                        const raw = e.target.value.replace(/[^\d.]/g, '');
                        // 只允许一个小数点
                        const parts = raw.split('.');
                        if (parts.length > 2) {
                            setRateStr(parts[0] + '.' + parts.slice(1).join(''));
                        } else {
                            setRateStr(raw);
                        }
                    }}
                    placeholder="请输入年利率"
                />
            </div>

            <div className="input-group">
                <label className="input-label">贷款年限</label>
                <select className="tool-select" value={years}
                    onChange={(e) => setYears(Number(e.target.value))}>
                    {[1, 5, 10, 15, 20, 25, 30].map((y) => (
                        <option key={y} value={y}>{y} 年</option>
                    ))}
                </select>
            </div>

            <div className="input-group">
                <label className="input-label">还款方式</label>
                <div className="button-group">
                    <button
                        className={`mode-button ${type === 'equal-installment' ? 'active' : ''}`}
                        onClick={() => setType('equal-installment')}
                    >
                        等额本息
                    </button>
                    <button
                        className={`mode-button ${type === 'equal-principal' ? 'active' : ''}`}
                        onClick={() => setType('equal-principal')}
                    >
                        等额本金
                    </button>
                </div>
            </div>

            <button className="tool-button primary" onClick={calculate}>
                计算
            </button>

            {result && (
                <>
                    <div className="info-box">
                        <h4>还款概览</h4>
                        <div className="timestamp-grid">
                            <div className="timestamp-item">
                                <span className="timestamp-label">月供{type === 'equal-principal' ? '（首月）' : ''}</span>
                                <span className="timestamp-value">¥{formatMoney(result.monthly)}</span>
                            </div>
                            <div className="timestamp-item">
                                <span className="timestamp-label">还款总额</span>
                                <span className="timestamp-value">¥{formatMoney(result.total)}</span>
                            </div>
                            <div className="timestamp-item">
                                <span className="timestamp-label">利息总额</span>
                                <span className="timestamp-value">¥{formatMoney(result.totalInterest)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="info-box">
                        <h4>前12个月还款明细</h4>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ color: 'var(--text-tertiary)', textAlign: 'left' }}>
                                        <th style={{ padding: '4px 8px' }}>月份</th>
                                        <th style={{ padding: '4px 8px' }}>月供</th>
                                        <th style={{ padding: '4px 8px' }}>本金</th>
                                        <th style={{ padding: '4px 8px' }}>利息</th>
                                        <th style={{ padding: '4px 8px' }}>剩余</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.schedule.map((row) => (
                                        <tr key={row.month}
                                            style={{ borderTop: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                                            <td style={{ padding: '4px 8px' }}>{row.month}</td>
                                            <td style={{ padding: '4px 8px' }}>¥{formatMoney(row.payment)}</td>
                                            <td style={{ padding: '4px 8px' }}>¥{formatMoney(row.principal)}</td>
                                            <td style={{ padding: '4px 8px' }}>¥{formatMoney(row.interest)}</td>
                                            <td style={{ padding: '4px 8px' }}>¥{formatMoney(row.balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MortgageCalculator;
